import { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { getConfigurations } from '../../services/api';
import ModelViewer from '../viewer3d/ModelViewer';
import ShareModal from '../share/ShareModal';
import styles from './ConfigLibraryModal.module.css';

/**
 * 配置库弹窗 - 浏览并应用配置库中的配置
 *
 * 优化设计：
 * - 移动端全屏体验，触摸友好的交互
 * - 复用 ModelViewer 组件展示3D模型
 * - 流畅的页面切换动画
 * - 清晰的视觉层次
 */
export default function ConfigLibraryModal({
    isOpen,
    onClose,
    onApplyConfig,
}) {
    const { t } = useLanguage();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // all | official | community | mine
    const [viewMode, setViewMode] = useState('image'); // image | model3d
    const [modelLoading, setModelLoading] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const scrollContainerRef = useRef(null);

    // 重置状态
    useEffect(() => {
        if (isOpen) {
            setSelectedConfig(null);
            setViewMode('image');
            setError('');
            setShowShareModal(false);
        }
    }, [isOpen]);

    // 加载配置列表
    useEffect(() => {
        if (!isOpen) return;

        const loadConfigs = async () => {
            setLoading(true);
            setError('');
            try {
                const result = await getConfigurations({
                    tab: activeTab,
                    sort: 'hot',
                    limit: 20
                });

                if (result.success) {
                    setConfigs(result.data || []);
                } else {
                    setError(result.message || t('configLibrary.networkError'));
                }
            } catch (err) {
                console.error('Load configs failed:', err);
                setError(t('configLibrary.networkError'));
            } finally {
                setLoading(false);
            }
        };

        loadConfigs();
    }, [isOpen, activeTab, t]);

    // 选择配置
    const handleSelectConfig = useCallback((config) => {
        setSelectedConfig(config);
        setViewMode('image');
        setModelLoading(false);
    }, []);

    // 应用配置
    const handleApplyConfig = useCallback(() => {
        if (selectedConfig?.config_data) {
            onApplyConfig(selectedConfig.config_data);
            onClose();
        }
    }, [selectedConfig, onApplyConfig, onClose]);

    // 关闭弹窗
    const handleClose = useCallback(() => {
        setSelectedConfig(null);
        setViewMode('image');
        onClose();
    }, [onClose]);

    // 点击遮罩关闭
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    }, [handleClose]);

    // 返回列表
    const handleBackToList = useCallback(() => {
        setSelectedConfig(null);
        setViewMode('image');
    }, []);

    // 切换预览模式
    const handleSwitchViewMode = useCallback((mode) => {
        if (mode === 'model3d') {
            setModelLoading(true);
        }
        setViewMode(mode);
    }, []);

    // 3D模型加载完成
    const handleModelLoad = useCallback(() => {
        setModelLoading(false);
    }, []);

    // 3D模型加载错误
    const handleModelError = useCallback(() => {
        setModelLoading(false);
        setViewMode('image');
    }, []);

    // 切换Tab
    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        // 滚动到顶部
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, []);

    // 打开分享弹窗
    const handleOpenShare = useCallback(() => {
        setShowShareModal(true);
    }, []);

    // 关闭分享弹窗
    const handleCloseShare = useCallback(() => {
        setShowShareModal(false);
    }, []);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                {/* 头部导航 */}
                <div className={styles.header}>
                    {selectedConfig ? (
                        <button className={styles.backBtn} onClick={handleBackToList}>
                            <span className={styles.backIcon}>←</span>
                            <span>{t('configLibrary.back')}</span>
                        </button>
                    ) : (
                        <h2 className={styles.title}>
                            {t('configLibrary.title')}
                        </h2>
                    )}
                    <button className={styles.closeBtn} onClick={handleClose}>
                        <span>×</span>
                    </button>
                </div>

                {/* 选项卡 - 仅在列表视图显示 */}
                {!selectedConfig && (
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab} ${activeTab === 'all' ? styles.active : ''}`}
                            onClick={() => handleTabChange('all')}
                        >
                            {t('configLibrary.tabs.featured')}
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'official' ? styles.active : ''}`}
                            onClick={() => handleTabChange('official')}
                        >
                            {t('configLibrary.tabs.official')}
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'community' ? styles.active : ''}`}
                            onClick={() => handleTabChange('community')}
                        >
                            {t('configLibrary.tabs.community')}
                        </button>
                        <button
                            className={`${styles.tab} ${activeTab === 'mine' ? styles.active : ''}`}
                            onClick={() => handleTabChange('mine')}
                        >
                            {t('configLibrary.tabs.mine')}
                        </button>
                    </div>
                )}

                {/* 主内容区域 */}
                <div className={styles.body} ref={scrollContainerRef}>
                    {/* 配置列表视图 */}
                    {!selectedConfig && (
                        <div className={styles.listView}>
                            {loading && (
                                <div className={styles.loading}>
                                    <div className={styles.spinner}></div>
                                    <span>{t('configLibrary.loading')}</span>
                                </div>
                            )}

                            {error && (
                                <div className={styles.error}>
                                    <span className={styles.errorIcon}>⚠️</span>
                                    <span>{error}</span>
                                    <button
                                        className={styles.retryBtn}
                                        onClick={() => handleTabChange(activeTab)}
                                    >
                                        {t('configLibrary.retry')}
                                    </button>
                                </div>
                            )}

                            {!loading && !error && configs.length === 0 && (
                                <div className={styles.empty}>
                                    <span className={styles.emptyIcon}>📭</span>
                                    <span>{t('configLibrary.empty')}</span>
                                </div>
                            )}

                            {!loading && !error && configs.length > 0 && (
                                <div className={styles.configList}>
                                    {configs.map((config) => (
                                        <div
                                            key={config.id}
                                            className={styles.configCard}
                                            onClick={() => handleSelectConfig(config)}
                                        >
                                            <div className={styles.configImage}>
                                                {config.cover_image_url ? (
                                                    <img src={config.cover_image_url} alt={config.title} />
                                                ) : (
                                                    <div className={styles.placeholderImage}>
                                                        <span>🏍️</span>
                                                    </div>
                                                )}
                                                {config.is_official && (
                                                    <span className={styles.officialBadge}>
                                                        {t('configLibrary.official')}
                                                    </span>
                                                )}
                                                {config.model3d_url && (
                                                    <span className={styles.modelBadge}>3D</span>
                                                )}
                                                {/* 在"我的配置"标签下显示状态徽章 */}
                                                {activeTab === 'mine' && config.status && config.status !== 'approved' && (
                                                    <span className={`${styles.statusBadge} ${styles[config.status]}`}>
                                                        {t(`configLibrary.status.${config.status}`)}
                                                    </span>
                                                )}
                                            </div>
                                            <div className={styles.configInfo}>
                                                <h3 className={styles.configTitle}>{config.title}</h3>
                                                <div className={styles.configMeta}>
                                                    <span className={styles.author}>
                                                        {config.user?.nickname || 'Admin'}
                                                    </span>
                                                    <span className={styles.price}>
                                                        ${(config.total_price || 0).toLocaleString()}
                                                    </span>
                                                </div>
                                                <div className={styles.configStats}>
                                                    <span>❤️ {config.likes_count || 0}</span>
                                                    <span>⭐ {config.favorites_count || 0}</span>
                                                    <span>💬 {config.comments_count || 0}</span>
                                                </div>
                                            </div>
                                            <div className={styles.cardArrow}>›</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 配置详情视图 */}
                    {selectedConfig && (
                        <div className={styles.detailView}>
                            {/* 预览区域 */}
                            <div className={styles.previewSection}>
                                {/* 预览模式切换 */}
                                {selectedConfig.model3d_url && (
                                    <div className={styles.viewModeToggle}>
                                        <button
                                            className={`${styles.modeBtn} ${viewMode === 'image' ? styles.active : ''}`}
                                            onClick={() => handleSwitchViewMode('image')}
                                        >
                                            📷 {t('configLibrary.preview.image')}
                                        </button>
                                        <button
                                            className={`${styles.modeBtn} ${viewMode === 'model3d' ? styles.active : ''}`}
                                            onClick={() => handleSwitchViewMode('model3d')}
                                        >
                                            🎮 {t('configLibrary.preview.model3d')}
                                        </button>
                                    </div>
                                )}

                                {/* 预览内容 */}
                                <div className={styles.previewContainer}>
                                    {viewMode === 'model3d' && selectedConfig.model3d_url ? (
                                        <div className={styles.modelViewerWrapper}>
                                            {modelLoading && (
                                                <div className={styles.modelLoading}>
                                                    <div className={styles.spinner}></div>
                                                    <span>{t('configLibrary.preview.loading3d')}</span>
                                                </div>
                                            )}
                                            <ModelViewer
                                                modelUrl={selectedConfig.model3d_url}
                                                onLoad={handleModelLoad}
                                                onError={handleModelError}
                                                backgroundColor="#1a1a1a"
                                                environmentPreset="city"
                                                showShadows={true}
                                                enableOrbit={true}
                                                enablePan={false}
                                                minDistance={2}
                                                maxDistance={20}
                                            />
                                            <div className={styles.modelHint}>
                                                👆 {t('configLibrary.preview.hint')}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={styles.imagePreview}>
                                            {selectedConfig.cover_image_url ? (
                                                <img src={selectedConfig.cover_image_url} alt={selectedConfig.title} />
                                            ) : (
                                                <div className={styles.placeholderImageLarge}>
                                                    <span>🏍️</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 配置信息 */}
                            <div className={styles.infoSection}>
                                <div className={styles.infoHeader}>
                                    <h3 className={styles.detailTitle}>{selectedConfig.title}</h3>
                                    {selectedConfig.is_official && (
                                        <span className={styles.officialTag}>
                                            {t('configLibrary.detail.officialTag')}
                                        </span>
                                    )}
                                </div>

                                <div className={styles.priceRow}>
                                    <span className={styles.priceLabel}>
                                        {t('configLibrary.detail.totalPrice')}
                                    </span>
                                    <span className={styles.priceValue}>
                                        ${(selectedConfig.total_price || 0).toLocaleString()}
                                    </span>
                                </div>

                                <div className={styles.metaRow}>
                                    <div className={styles.authorInfo}>
                                        <span className={styles.authorLabel}>
                                            {t('configLibrary.detail.creator')}
                                        </span>
                                        <span className={styles.authorName}>
                                            {selectedConfig.user?.nickname || 'Admin'}
                                        </span>
                                    </div>
                                    <div className={styles.statsInfo}>
                                        <span>❤️ {selectedConfig.likes_count || 0}</span>
                                        <span>⭐ {selectedConfig.favorites_count || 0}</span>
                                        <span>💬 {selectedConfig.comments_count || 0}</span>
                                    </div>
                                </div>

                                {selectedConfig.description && (
                                    <p className={styles.description}>
                                        {selectedConfig.description}
                                    </p>
                                )}

                                {/* 配置详情 */}
                                {selectedConfig.config_data && (
                                    <div className={styles.configDetails}>
                                        <h4 className={styles.detailsTitle}>
                                            {t('configLibrary.detail.configDetails')}
                                        </h4>
                                        <div className={styles.detailsList}>
                                            {selectedConfig.config_data.body && (
                                                <div className={styles.detailItem}>
                                                    <span className={styles.detailLabel}>
                                                        {t('configLibrary.detail.bodyColor')}
                                                    </span>
                                                    <span className={styles.detailValue}>
                                                        {selectedConfig.config_data.body.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedConfig.config_data.trim && (
                                                <div className={styles.detailItem}>
                                                    <span className={styles.detailLabel}>
                                                        {t('configLibrary.detail.trimStyle')}
                                                    </span>
                                                    <span className={styles.detailValue}>
                                                        {selectedConfig.config_data.trim.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedConfig.config_data.comfort?.length > 0 && (
                                                <div className={styles.detailItem}>
                                                    <span className={styles.detailLabel}>
                                                        {t('configLibrary.detail.comfort')}
                                                    </span>
                                                    <span className={styles.detailValue}>
                                                        {selectedConfig.config_data.comfort.length} {t('configLibrary.detail.items')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedConfig.config_data.performance && selectedConfig.config_data.performance !== 'none' && (
                                                <div className={styles.detailItem}>
                                                    <span className={styles.detailLabel}>
                                                        {t('configLibrary.detail.performance')}
                                                    </span>
                                                    <span className={styles.detailValue}>
                                                        {Array.isArray(selectedConfig.config_data.performance)
                                                            ? `${selectedConfig.config_data.performance.length} ${t('configLibrary.detail.items')}`
                                                            : selectedConfig.config_data.performance.replace(/_/g, ' ')}
                                                    </span>
                                                </div>
                                            )}
                                            {selectedConfig.config_data.style?.length > 0 && (
                                                <div className={styles.detailItem}>
                                                    <span className={styles.detailLabel}>
                                                        {t('configLibrary.detail.style')}
                                                    </span>
                                                    <span className={styles.detailValue}>
                                                        {selectedConfig.config_data.style.length} {t('configLibrary.detail.items')}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* 底部操作栏 - 仅在详情视图显示 */}
                {selectedConfig && (
                    <div className={styles.footer}>
                        <div className={styles.footerActions}>
                            <button
                                className={styles.shareBtn}
                                onClick={handleOpenShare}
                                title={t('configLibrary.share')}
                            >
                                <svg className={styles.shareIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46696 6.6582 8 5.66667 8C4.19391 8 3 9.19391 3 10.6667C3 12.1394 4.19391 13.3333 5.66667 13.3333C6.6582 13.3333 7.54305 12.8663 8.08261 12.1426L15.0227 15.9627C15.0077 16.0842 15 16.2078 15 16.3333C15 17.806 16.194 19 17.6667 19C19.1394 19 20.3333 17.806 20.3333 16.3333C20.3333 14.8607 19.1394 13.6667 17.6667 13.6667C16.6752 13.6667 15.7904 14.1337 15.2508 14.8574L8.31073 11.0373C8.32566 10.9159 8.33333 10.7922 8.33333 10.6667C8.33333 10.5412 8.32566 10.4175 8.31073 10.296L15.2508 6.47593C15.7904 7.19968 16.6752 7.66667 17.6667 7.66667C17.7855 7.66667 17.903 7.66027 18.0189 7.64778" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <button
                                className={styles.applyBtn}
                                onClick={handleApplyConfig}
                                disabled={!selectedConfig.config_data}
                            >
                                <span className={styles.applyIcon}>✨</span>
                                <span>{t('configLibrary.applyConfig')}</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* 分享弹窗 */}
                <ShareModal
                    isOpen={showShareModal}
                    onClose={handleCloseShare}
                    configId={selectedConfig?.id}
                    configData={selectedConfig?.config_data}
                    coverImageUrl={selectedConfig?.cover_image_url}
                    totalPrice={selectedConfig?.total_price}
                    configTitle={selectedConfig?.title}
                />
            </div>
        </div>
    );
}
